import { Box, Grid, Typography } from '@material-ui/core';
import { lighten, makeStyles } from '@material-ui/core/styles';
import { Layout } from '@roc/feature-app-core';
import { ConfirmDialog, Paper, SelectField, TextField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useLoanSubmissionRoutes } from '@roc/feature-loans';
import {
  capitalProviderCover,
  capitalProviderOtherCover,
  fixFlipCover,
  groundUpCover,
  otherLoanTypeCover,
  rentalPortfolioCover,
  singlePropertyCover,
  stabilizedBridgeCover,
} from '../assets';
import { useThirdPartyLoanRoutes } from '../hooks/useThirdPartyLoanRoutes';
import { useThirdPartyLoanStore } from '../hooks/useThirdPartyLoanStore';
import { CapitalProviderInfo } from './common/capitalProviderInfo';

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(4),
  },
  loanTypePaper: {
    padding: theme.spacing(4),
    border: `1px solid ${theme.palette.primary.light}`,
    '&:hover': {
      background: lighten(theme.palette.primary.main, 0.9),
    },
    height: '100%',
  },
}));

export const CapitalProviderSelector = () => {
  const { loanSubmissionRoutesConfig } = useLoanSubmissionRoutes();
  const { thirdPartyLoanRoutesConfig } = useThirdPartyLoanRoutes();
  const { thirdPartyLoansStore } = useThirdPartyLoanStore();
  const { push } = useHistory();
  const [showCapitalProviderDialog, setShowCapitalProviderDialog] = useState(
    false
  );

  useEffect(() => {
    thirdPartyLoansStore.setCapitalProviderName(null);
  }, []);

  return (
    <>
      <CapitalProviderOptions
        onCapitalProviderClick={() =>
          push(loanSubmissionRoutesConfig.submitloan.url)
        }
        onOtherLenderClick={() => setShowCapitalProviderDialog(true)}
      />
      <CapitalProviderDialog
        open={showCapitalProviderDialog}
        onClose={() => setShowCapitalProviderDialog(false)}
        onSubmit={capitalProviderName => {
          setShowCapitalProviderDialog(false);
          thirdPartyLoansStore.setCapitalProviderName(capitalProviderName);
          push(thirdPartyLoanRoutesConfig.submitloan.url);
        }}
      />
    </>
  );
};

const CapitalProviderOptions = ({
  onCapitalProviderClick,
  onOtherLenderClick,
}) => {
  const classes = useStyles();

  return (
    <Layout maxWidth={'lg'}>
      <Typography
        align="center"
        variant="h2"
        style={{
          color: 'gray',
          marginBottom: '3rem',
        }}
      >
        Submit Loan
      </Typography>
      <Paper className={classes.paper}>
        <Box mb={4}>
          <Typography variant="h4" style={{ textAlign: 'center' }}>
            Who are you planning to fund this loan with?
          </Typography>
        </Box>
        <Box mb={4}>
          {/* <Typography align="left" variant="h3">
            Select a Product
          </Typography> */}
          <Box mx={'auto'} mb={4} mt={4}>
            <Grid container direction="row" justifyContent="center" spacing={2}>
              <Grid item>
                <ProductCard
                  name={'Platform Capital'}
                  text={<></>}
                  image={capitalProviderCover}
                  onClick={onCapitalProviderClick}
                />
              </Grid>
              <Grid item>
                <ProductCard
                  name={'External Capital'}
                  text={<></>}
                  image={capitalProviderOtherCover}
                  onClick={onOtherLenderClick}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>
    </Layout>
  );
};

export const LoanTypeOptions = () => {
  const classes = useStyles();
  const { thirdPartyLoansStore } = useThirdPartyLoanStore();
  const capitalProviderName = thirdPartyLoansStore.capitalProviderName;

  const { thirdPartyLoanRoutesConfig } = useThirdPartyLoanRoutes();
  const [showOtherTypeDialog, setShowOtherTypeDialog] = useState(false);
  const [showCapitalProviderDialog, setShowCapitalProviderDialog] = useState(capitalProviderName === undefined);

  return (
    <>
      <CapitalProviderDialog
        open={showCapitalProviderDialog}
        onClose={() => {
          thirdPartyLoansStore.setCapitalProviderName('');
          setShowCapitalProviderDialog(false);
        }}
        onSubmit={name => {
          thirdPartyLoansStore.setCapitalProviderName(name);
          setShowCapitalProviderDialog(false);
        }}
      />
      <Layout maxWidth={'lg'}>
        <Grid
          container
          direction="row"
          spacing={2}
          style={{ marginBottom: '3rem' }}
        >
          <Grid item xs={12} sm={8}>
            <Typography
              align="left"
              variant="h2"
              style={{
                color: 'gray',
              }}
            >
              Submit a Loan
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <CapitalProviderInfo capitalProvider={capitalProviderName} />
          </Grid>
        </Grid>
        <Paper className={classes.paper}>
          <Box mb={4}>
            <Box mx={'auto'} mb={4} mt={4}>
              <Grid container direction="row" justifyContent="center" spacing={2}>
                <Grid item>
                  <ProductCard
                    name={'Fix & Flip'}
                    text={
                      <>
                        Short-term financing to purchase and/or renovate, then
                        sell or rent, non-owner occupied homes with 1-4 units.
                      </>
                    }
                    url={thirdPartyLoanRoutesConfig.fixFlip().url}
                    image={fixFlipCover}
                  />
                </Grid>
                <Grid item>
                  <ProductCard
                    name={'Ground Up Construction'}
                    text={
                      <>Short-term financing to build new homes with 1-4 units.</>
                    }
                    url={thirdPartyLoanRoutesConfig.groundUp().url}
                    image={groundUpCover}
                  />
                </Grid>
                <Grid item>
                  <ProductCard
                    name={'Stabilized Property'}
                    text={
                      <>
                        Short-term financing for recently renovated or constructed
                        properties that are currently, or soon to be, listed for
                        sale.
                      </>
                    }
                    url={thirdPartyLoanRoutesConfig.stabilizedBridge().url}
                    image={stabilizedBridgeCover}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box mx={'auto'} mb={4} mt={4}>
              <Grid container direction="row" justifyContent="center" spacing={4}>
                <Grid item>
                  <ProductCard
                    name={'Single Property Rental'}
                    text={
                      <>
                        Long-term financing to purchase or refinance a non-owner
                        occupied rental property with 1-4 units.
                      </>
                    }
                    url={thirdPartyLoanRoutesConfig.singleProperty().url}
                    image={singlePropertyCover}
                  />
                </Grid>
                <Grid item>
                  <ProductCard
                    name={'Rental Portfolio'}
                    text={
                      <>
                        Long-term financing to purchase or refinance multiple
                        non-owner occupied rental properties with 1-4 units.
                      </>
                    }
                    url={thirdPartyLoanRoutesConfig.rentalPortfolio().url}
                    image={rentalPortfolioCover}
                  />
                </Grid>
                <Grid item>
                  <ProductCard
                    name={'Other Loan Type'}
                    text={
                      <>
                        Long-term financing to purchase or refinance a non-owner
                        occupied rental property with 1-4 units.
                      </>
                    }
                    onClick={() => setShowOtherTypeDialog(true)}
                    image={otherLoanTypeCover}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Paper>
        <OtherLoanTypeDialog
          open={showOtherTypeDialog}
          onClose={() => setShowOtherTypeDialog(false)}
        />
      </Layout>
    </>
  );
};

const ProductCard = observer(
  ({ onClick = null, url = null, image, name, text }) => {
    const classes = useStyles();
    return (
      <Link to={url} onClick={onClick}>
        <Box
          style={{
            border: '1px solid #5E5E5E4D',
            borderRadius: '14px',
            width: '340px',
            height: '340px',
          }}
        >
          <Box
            height="70px"
            paddingLeft="20px"
            paddingRight="20px"
            display="flex"
            alignItems="center"
          >
            <Typography
              variant="h6"
              style={{ fontSize: '24px', color: 'black' }}
            >
              {name}
            </Typography>
          </Box>
          <Box style={{ height: '132.2px', objectFit: 'cover' }}>
            <img
              src={image}
              width={'100%'}
              style={{ objectFit: 'cover', height: '100%' }}
            />
          </Box>
          <Box paddingLeft="20px" paddingRight="20px" marginTop="10px">
            <Typography
              variant="body2"
              style={{ fontSize: '14px', color: 'black', fontWeight: 'bold' }}
            >
              {text}
            </Typography>
          </Box>
        </Box>
      </Link>
    );
  }
);

const CapitalProviderDialog = observer(({ open, onClose, onSubmit }) => {
  const [capitalProviderName, setCapitalProviderName] = useState('');
  return (
    <ConfirmDialog
      open={open}
      title={'Loan Submission with Other Lender'}
      handleCancel={onClose}
      handleClose={onClose}
      handleConfirm={() => onSubmit(capitalProviderName)}
      confirmButtonText="Continue"
      removeContentBox
      body={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            Which capital provider is getting this loan? This is an{' '}
            <b>optional</b>
            &nbsp;step.
          </Grid>
          <Grid item xs={12}>
            <TextField
              testId="capital-provider-name"
              label="Capital Provider"
              variant="outlined"
              fullWidth
              onChange={e => setCapitalProviderName(e.target.value)}
              value={capitalProviderName}
            />
          </Grid>
        </Grid>
      }
    />
  );
});


const OtherLoanTypeDialog = observer(({ open, onClose }) => {
  const [type, setType] = useState('');
  const { thirdPartyLoanRoutesConfig } = useThirdPartyLoanRoutes();
  const { push } = useHistory();

  const handleConfirm = () => {
    if (type === 'bridge') {
      push(thirdPartyLoanRoutesConfig.otherBridge().url);
      onClose();
    } else if (type === 'rental') {
      push(thirdPartyLoanRoutesConfig.otherRental().url);
      onClose();
    }
  };

  return (
    <ConfirmDialog
      open={open}
      title={'Other Loan Type'}
      handleCancel={onClose}
      handleClose={onClose}
      handleConfirm={handleConfirm}
      confirmButtonText="Continue"
      removeContentBox
      body={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            What type of loan is this?
          </Grid>
          <Grid item xs={12}>
            <SelectField
              testId="capital-provider-name"
              label="Loan Type"
              variant="outlined"
              fullWidth
              onChange={value => setType(value)}
              value={type}
              options={[
                { label: 'Short Term Loan (up to 3 years)', value: 'bridge' },
                { label: 'Long Term Loan (over 3 years)', value: 'rental' },
              ]}
            />
          </Grid>
        </Grid>
      }
    />
  );
});
